<template>
  <div>
    <HeaderNavbar
      :title="$t('settings.swimlanes.title')"
      :breadcrumbs="[
        { label: $t('nav.settings'), path: { name: 'settings' } },
        { label: $t('settings.swimlanes.title'), path: { name: 'settings.swimlanes' } },
      ]" />
    <div class="container-lead">
      <div class="mt-3 responsive-padding">
        <p class="text-muted">{{ $t('settings.swimlanes.description') }}</p>

        <div class="card swimlane-card">
          <div class="card-body">
            <div v-if="isLoading" class="text-center text-muted py-4">
              <div class="spinner-border spinner-border-sm" role="status"></div>
            </div>

            <template v-else>
              <ul ref="listRef" :key="listVersion" class="swimlane-list">
                <li
                  v-for="lane in swimlanes"
                  :key="lane.lane_key"
                  :data-lane-key="lane.lane_key"
                  class="swimlane-row"
                  :class="lane.is_system ? 'swimlane-row--system' : 'swimlane-row--custom'">
                  <span class="swimlane-grip">
                    <IconGripVertical
                      v-if="!lane.is_system"
                      :size="18"
                      class="swimlane-drag-handle"
                      :aria-label="$t('settings.swimlanes.reorder', { name: lane.name })" />
                    <IconLock
                      v-else
                      :size="16"
                      class="swimlane-lock"
                      v-tooltip="$t('settings.swimlanes.system_locked')" />
                  </span>

                  <LeadInput
                    v-if="editingKey === lane.lane_key"
                    v-model="editingName"
                    class="swimlane-name-field"
                    :disabled="isSaving"
                    @keyup.enter="saveRename(lane)"
                    @keyup.esc="cancelRename" />
                  <span v-else class="swimlane-name text-truncate">{{ lane.name }}</span>

                  <span v-if="lane.is_hidden" class="badge swimlane-system-badge">{{
                    $t('settings.swimlanes.hidden')
                  }}</span>
                  <span v-if="lane.is_system" class="badge swimlane-system-badge">{{
                    $t('settings.swimlanes.system')
                  }}</span>
                  <span class="swimlane-count badge">{{
                    $t('settings.swimlanes.candidate_count', { count: lane.count })
                  }}</span>

                  <div class="swimlane-actions">
                    <template v-if="editingKey === lane.lane_key">
                      <LeadButton
                        variant="icon"
                        :disabled="isSaving"
                        :icon-start="IconCheck"
                        @click="saveRename(lane)" />
                      <LeadButton variant="icon" :disabled="isSaving" :icon-start="IconX" @click="cancelRename" />
                    </template>
                    <template v-else-if="!lane.is_system">
                      <LeadButton
                        variant="icon"
                        :disabled="isSaving"
                        :icon-start="IconPencil"
                        v-tooltip="$t('settings.swimlanes.rename')"
                        @click="startRename(lane)" />
                      <LeadButton
                        variant="icon"
                        class="swimlane-delete-btn"
                        :disabled="isSaving || lane.count > 0"
                        :icon-start="IconTrash"
                        v-tooltip="lane.count > 0 ? $t('settings.swimlanes.delete_has_candidates') : $t('btn.delete')"
                        @click="promptDelete(lane)" />
                    </template>
                  </div>
                </li>
              </ul>

              <div class="swimlane-add">
                <LeadInput
                  v-model="newName"
                  class="swimlane-name-field"
                  :placeholder="$t('settings.swimlanes.add_placeholder')"
                  :disabled="isSaving"
                  @keyup.enter="addSwimlane" />
                <LeadButton
                  variant="white"
                  :disabled="isSaving || !newName.trim()"
                  :icon-start="IconPlus"
                  @click="addSwimlane">
                  {{ $t('settings.swimlanes.add_button') }}
                </LeadButton>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <LiConfirmationDialog
      v-model:dialogShow="deleteDialog"
      :confirmButtonLabel="$t('btn.delete')"
      @onContinue="deleteConfirmed">
      <h1 class="mb-3">{{ $t('settings.swimlanes.delete_title') }}</h1>
      <p>{{ $t('settings.swimlanes.delete_text', { name: laneToDelete?.name ?? '' }) }}</p>
    </LiConfirmationDialog>
  </div>
</template>
<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { createSwimlane, deleteSwimlane, fetchSwimlanes, renameSwimlane, reorderSwimlanes } from '@/api/candidates';
import type { Swimlane } from '@/types/kanban';
import { useUserStore } from '@/stores/user';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import HeaderNavbar from '@/components/header/HeaderNavbar.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import LeadInput from '@/components/form/LeadInput.vue';
import { ConfirmationDialog as LiConfirmationDialog } from '@prospective/lithium';
import { IconCheck, IconGripVertical, IconLock, IconPencil, IconPlus, IconTrash, IconX } from '@tabler/icons-vue';
import Sortable from '@/utils/sortable';

/* ---------------------------------- STATE --------------------------------- */
const router = useRouter();
const userStore = useUserStore();
const featureStore = useCustomerFeaturesStore();

const swimlanes = ref<Swimlane[]>([]);
const isLoading = ref(true);
const isSaving = ref(false);
const newName = ref('');
const editingKey = ref<string | null>(null);
const editingName = ref('');
const deleteDialog = ref(false);
const laneToDelete = ref<Swimlane | null>(null);
const listVersion = ref(0);
const listRef = ref<HTMLElement | null>(null);

const isAdmin = computed(() => Boolean(userStore.currentUser?.is_admin));

/* --------------------------------- METHODS -------------------------------- */
const sortByPosition = (lanes: Swimlane[]): Swimlane[] => [...lanes].sort((a, b) => a.position - b.position);

const applyList = (lanes: Swimlane[]) => {
  swimlanes.value = sortByPosition(lanes);
  listVersion.value++;
};

const loadSwimlanes = async () => {
  isLoading.value = true;
  try {
    applyList(await fetchSwimlanes());
  } finally {
    isLoading.value = false;
  }
};

/* ---------------------------------- ADD ----------------------------------- */
const addSwimlane = async () => {
  const name = newName.value.trim();
  if (!name || isSaving.value) return;
  isSaving.value = true;
  try {
    applyList(await createSwimlane(name));
    newName.value = '';
  } catch (error) {
    console.error('Failed to create swimlane:', error);
  } finally {
    isSaving.value = false;
  }
};

/* --------------------------------- RENAME --------------------------------- */
const startRename = async (lane: Swimlane) => {
  editingKey.value = lane.lane_key;
  editingName.value = lane.name;
  await nextTick();
  // focus the inline input
  listRef.value?.querySelector<HTMLInputElement>('.swimlane-name-field input')?.focus();
};

const cancelRename = () => {
  editingKey.value = null;
  editingName.value = '';
};

const saveRename = async (lane: Swimlane) => {
  const name = editingName.value.trim();
  if (!name || isSaving.value) return;
  if (name === lane.name) return cancelRename();
  isSaving.value = true;
  try {
    applyList(await renameSwimlane(lane.lane_key, name));
    cancelRename();
  } catch (error) {
    console.error('Failed to rename swimlane:', error);
  } finally {
    isSaving.value = false;
  }
};

/* --------------------------------- DELETE --------------------------------- */
const promptDelete = (lane: Swimlane) => {
  laneToDelete.value = lane;
  deleteDialog.value = true;
};

const deleteConfirmed = async () => {
  const lane = laneToDelete.value;
  if (!lane) return;
  isSaving.value = true;
  try {
    applyList(await deleteSwimlane(lane.lane_key));
  } catch (error) {
    console.error('Failed to delete swimlane:', error);
  } finally {
    isSaving.value = false;
    laneToDelete.value = null;
  }
};

/* -------------------------------- REORDER --------------------------------- */
let sortable: Sortable | null = null;

const handleReorder = async () => {
  const orderedKeys = [...(listRef.value?.querySelectorAll<HTMLElement>('.swimlane-row--custom') ?? [])]
    .map((el) => el.dataset.laneKey)
    .filter((key): key is string => Boolean(key));

  const byKey = new Map(swimlanes.value.map((lane) => [lane.lane_key, lane]));
  const orderedCustoms = orderedKeys.map((key) => byKey.get(key)).filter((lane): lane is Swimlane => Boolean(lane));
  let customIndex = 0;
  const snapshot = swimlanes.value;
  swimlanes.value = swimlanes.value.map((lane) => (lane.is_system ? lane : orderedCustoms[customIndex++]));
  listVersion.value++;

  isSaving.value = true;
  try {
    applyList(await reorderSwimlanes(orderedKeys));
  } catch (error) {
    console.error('Failed to reorder swimlanes:', error);
    swimlanes.value = snapshot;
    listVersion.value++;
  } finally {
    isSaving.value = false;
  }
};

const destroySortable = () => {
  sortable?.destroy();
  sortable = null;
};

watch(listRef, (el) => {
  destroySortable();
  if (!el) return;
  sortable = Sortable.create(el, {
    draggable: '.swimlane-row',
    handle: '.swimlane-drag-handle',
    filter: '.swimlane-row--system',
    animation: 150,
    // keep custom lanes between the pinned system lanes (New on top, Hired/Closed at the bottom)
    onMove: (event) => !(event.related as HTMLElement).classList.contains('swimlane-row--system'),
    onEnd: handleReorder,
  });
});

/* ----------------------------- LIFECYCLE HOOKS ---------------------------- */
onMounted(() => {
  // admin-only page; also requires the ec_data_vault feature (candidate data collection)
  if (!isAdmin.value || !featureStore.featureEcDataVault) {
    router.replace({ name: 'settings' });
    return;
  }
  loadSwimlanes();
});
</script>
<style scoped>
.swimlane-card {
  max-width: 640px;
}

.swimlane-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.swimlane-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #fff;
  min-height: 60px;
}

.swimlane-row--system {
  background: #f9fafb;
}

.swimlane-grip {
  display: inline-flex;
  width: 18px;
  justify-content: center;
  flex: 0 0 auto;
}

.swimlane-drag-handle {
  color: #98a2b3;
  cursor: grab;
}

.swimlane-drag-handle:active {
  cursor: grabbing;
}

.swimlane-lock {
  color: #98a2b3;
}

.swimlane-name {
  flex: 1;
  font-weight: 600;
  color: #101828;
  min-width: 0;
}

.swimlane-name-field {
  flex: 1;
  min-width: 0;
}

.swimlane-system-badge {
  background: #eaecf0;
  color: #475467;
  font-weight: 500;
}

.swimlane-count {
  background: #f2f4f7;
  color: #475467;
  border-radius: 16px;
  font-weight: 500;
}

.swimlane-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

.swimlane-delete-btn:not(:disabled):hover {
  color: #d92d20;
  border-color: #fda29b;
}

.swimlane-add {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
}
</style>
